import { useState, type ReactNode } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useLiveQuery } from 'dexie-react-hooks'
import { useI18n, useFormat } from '@/i18n'
import { useActor } from '@/auth/pin/phoneSession'
import { hasAnyRole, ROLES } from '@/auth/roles'
import { readSitePack, roster, useDeviceInfo, useSyncStatus, type SitePack } from '@/offline'
import { InstallBanner } from '@/app/pwa/InstallBanner'
import { Icon, ItemRow, Strip, V1Header, V1Screen } from '@/features/gate/v1/kit'
import { C } from '@/features/gate/v1/style'
import { gateTiles } from '@/features/gate/routes'
import { storeTiles } from '@/features/store/routes'
import { processTiles } from '@/features/process/routes'
import { packTiles } from '@/features/pack/routes'
import { useStoreScope } from '@/features/store/v1/ByScope'
import type { PhoneTileDef } from '../tiles'
import { listWords } from '../listWords'

const KEEPER_ROLES = [ROLES.STORE_KEEPER, ROLES.SUPERVISOR]
const OPERATOR_ROLES = [ROLES.MACHINE_OPERATOR, ROLES.SUPERVISOR]

const ALL_TILES: PhoneTileDef[] = [...gateTiles, ...processTiles, ...packTiles, ...storeTiles]

/** What the site pack says this site has: machines (processing) and / or a pack line. */
function siteHas(pack: SitePack | null | undefined): { PROCESS: boolean; PACK: boolean } {
  if (!pack) return { PROCESS: false, PACK: false }
  return {
    PROCESS:
      pack.machines.length > 0 ||
      pack.runs.some((r) => r.step_code !== 'DELABEL') ||
      pack.issue_tasks.length > 0,
    PACK: pack.site_kind === 'PACKING' || pack.pack_runs.length > 0 || pack.pack_lots.length > 0,
  }
}

/**
 * Home, in the alignment map's look (the V1 boards). A Seva Mandir V1 village phone goes straight to
 * the board's आज screen; store, processing and packing phones list their jobs here.
 */
export function PhoneHomePage() {
  const { t } = useI18n()
  const fmt = useFormat()
  const actor = useActor()
  const info = useDeviceInfo()
  const sync = useSyncStatus()
  const navigate = useNavigate()
  const [today] = useState(() => Date.now())
  const teamKind = info.registration?.team.kind
  const sitePack = useLiveQuery(async () => (await readSitePack())?.pack ?? null, [], null)
  const gateConfig = useLiveQuery(() => roster.gateConfig(), [])
  const storeScope = useStoreScope()
  const has = siteHas(sitePack)
  const words = listWords(teamKind)

  if (teamKind === 'VILLAGE' && gateConfig?.v1) return <Navigate to="/m/gate" replace />
  // A V1 processing phone goes straight to the board's आज (the run on the machine).
  if (
    teamKind === 'SITE' &&
    storeScope?.v1 &&
    storeScope.siteKind === 'PROCESSING' &&
    hasAnyRole(actor.roles, OPERATOR_ROLES)
  )
    return <Navigate to="/m/process" replace />
  // A V1 packing hall: the operator to the pack line's आज, the keeper to the hall (dispatch; trucks
  // from the godown are received from there).
  if (
    teamKind === 'SITE' &&
    storeScope?.v1 &&
    (storeScope.siteKind === 'PACKING' || sitePack?.site_kind === 'PACKING')
  ) {
    if (hasAnyRole(actor.roles, OPERATOR_ROLES)) return <Navigate to="/m/pack" replace />
    if (hasAnyRole(actor.roles, KEEPER_ROLES)) return <Navigate to="/m/pack/hall" replace />
  }
  // A V1 store keeper goes straight to the board's गोदाम · आज (vehicles on the way, unload, send):
  // at the godown, and at a processing site or packing hall where trucks from the godown arrive.
  if (
    teamKind === 'SITE' &&
    storeScope?.v1 &&
    ['WAREHOUSE', 'PROCESSING', 'PACKING'].includes(storeScope.siteKind ?? '') &&
    hasAnyRole(actor.roles, KEEPER_ROLES)
  )
    return <Navigate to="/m/store" replace />

  const tiles = ALL_TILES.filter(
    (tile) =>
      (!teamKind || tile.team === teamKind) &&
      hasAnyRole(actor.roles, tile.roles) &&
      (!tile.needs || has[tile.needs]),
  )
  const sections: [string, PhoneTileDef[]][] = [
    ['home.gate', tiles.filter((x) => x.to.startsWith('/m/gate'))],
    ['pack.home.section', tiles.filter((x) => x.to.startsWith('/m/pack'))],
    ['process.home.section', tiles.filter((x) => x.to.startsWith('/m/process'))],
    ['home.store', tiles.filter((x) => x.to.startsWith('/m/store'))],
  ]

  return (
    <V1Screen
      nav
      header={
        <V1Header
          pending={sync.pending}
          updatable
          icon="home"
          title={t('home.greeting', { name: actor.name.split(' ')[0] ?? actor.name })}
        />
      }
    >
      <span style={{ fontSize: 16, color: C.muted, lineHeight: 1.3 }}>
        {fmt.date(today)}
        {info.rosterDownloadedAt &&
          ` · ${t(words.age, { when: fmt.relative(info.rosterDownloadedAt) })}`}
      </span>
      {info.freshness === 'expired' && <Strip icon="bang">{t(words.expired)}</Strip>}
      {info.freshness === 'stale' && <Strip icon="bang">{t(words.stale)}</Strip>}
      <InstallBanner />
      {sections.map(
        ([title, list]) =>
          list.length > 0 && (
            <section key={title} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <h2 style={{ margin: '4px 0 0', fontSize: 18, lineHeight: 1.25 }}>
                {t(title as Parameters<typeof t>[0])}
              </h2>
              {list.map((tile) => (
                <ItemRow
                  key={tile.to}
                  onClick={() => navigate(tile.to)}
                  border={tile.primary ? C.acc : C.rule}
                  borderWidth={tile.primary ? 2 : 1.5}
                  padding="8px 10px"
                  titleSize={18}
                  lead={<TileIcon>{<tile.icon size={28} />}</TileIcon>}
                  title={t(tile.title)}
                  line={<span style={{ fontSize: 15, color: C.muted }}>{t(tile.hint)}</span>}
                  right={
                    <span style={{ color: C.acc, display: 'flex' }}>
                      <Icon name="next" size={24} sw={2.4} />
                    </span>
                  }
                />
              ))}
            </section>
          ),
      )}
    </V1Screen>
  )
}

/** The beige square behind a job's icon (as on the boards' set-up row). */
function TileIcon({ children }: { children: ReactNode }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 48,
        height: 48,
        flexShrink: 0,
        borderRadius: 12,
        background: C.sunk,
        color: C.acc,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {children}
    </span>
  )
}
